import type { GroupCreateSchema } from '@repo/schemas/group/groupCreate';
import type { GroupScheduleSchema } from '@repo/schemas/group/groupSchedule';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useEditGroup } from '@repo/api/group/useEditGroup';
import { usePostGroup } from '@repo/api/group/usePostGroup';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { countStudents } from '@repo/lib/user-lvl/countStudents';
import { groupCreateSchema } from '@repo/schemas/group/groupCreate';
import { CheckCircle2Icon, ChevronDownIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { GroupScheduleDialog } from '@/components/features/manage/manage-groups/GroupScheduleDialog';
import { GroupDialogHeader, GroupUpsertDetailsForm, SelectedUsersSummary, SlotsCounter, useGroupUsersFormState, UsersSelectList } from '@/components/features/manage/manage-groups/GroupUpsertForm';
import { Button, ButtonGroup } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the CreateGroup component.
 * @property {boolean} [open] - Whether the dialog is open.
 * @property {(bool: boolean) => void} [onOpenChange] - Callback to change dialog open state.
 */
interface CreateGroupDialogProps {
  open?: boolean;
  onOpenChange?: (bool: boolean) => void;
}

export default function CreateGroupDialog(createGroupDialogProps: CreateGroupDialogProps) {
  const { open, onOpenChange } = createGroupDialogProps;
  const [openScheduleDialog, setOpenScheduleDialog] = useState<boolean>(false);
  const [pendingGroupData, setPendingGroupData] = useState<GroupCreateSchema | null>(null);
  const [pendingUserIds, setPendingUserIds] = useState<number[]>([]);
  const {
    tabs,
    setTabs,
    isMainTab,
    searchUserValue,
    setSearchUserValue,
    selectedUsers,
    users,
    usersErrorMessage,
    usersIsEmpty,
    usersNoResults,
    getUsersInfinite,
    onRemoveUser,
    onTabsValueChange,
    setSelectedUsersFromIds,
    resetState,
  } = useGroupUsersFormState();

  const { mutateAsync: addGroup, isPending: postIsPending } = usePostGroup();
  const { mutateAsync: editGroup, isPending: patchIsPending } = useEditGroup();

  const isPending = postIsPending || patchIsPending;

  const resetCreateFormState = () => {
    resetState();
    form.reset();
  };

  const handleClose = () => {
    onOpenChange?.(false);
    resetCreateFormState();
  };

  const createGroupWithMembers = async (data: GroupCreateSchema, userIds: number[]) => {
    const encodedPayload = groupCreateSchema.decode(data);

    return await addGroup(encodedPayload)
      .then(async (response) => {
        await editGroup({
          id: response.id,
          memberChanges: [{ userIds, add: true }],
        })
          .then(() => {
            return true;
          })
          .catch((error) => {
            toasterMessageHandler(baseErrorMessages, error);
            throw error;
          });
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
        throw error;
      });
  };

  const onSubmit = async (data: GroupCreateSchema, userIds: number[]) => {
    try {
      await createGroupWithMembers(data, userIds);
    } catch {
      return;
    }
    resetCreateFormState();
    onOpenChange?.(false);
  };

  const handleSaveAction = (saveAction: 'save' | 'saveAndOpenSchedule') => {
    void form.handleSubmit(async (data) => {
      const selectedIds = selectedUsers.map((u) => u.id);

      if (saveAction === 'save') {
        await onSubmit(data, selectedIds);
        return;
      }

      setPendingGroupData(data);
      setPendingUserIds(selectedIds);
      setOpenScheduleDialog(true);
    })();
  };

  const handleScheduleSubmit = async (schedule: GroupScheduleSchema) => {
    if (!pendingGroupData) return;

    await onSubmit(
      {
        ...pendingGroupData,
        schedule,
      },
      pendingUserIds
    );

    setPendingGroupData(null);
    setPendingUserIds([]);
  };

  const form = useForm<GroupCreateSchema>({
    defaultValues: {
      name: '',
      desc: '',
      slots: null,
      schedule: null,
    },
    mode: 'onBlur',
    resolver: standardSchemaResolver(groupCreateSchema.in),
  });

  const slotsValue =
    useWatch({
      control: form.control,
      name: 'slots',
    }) ?? undefined;

  return (
    <>
      <GroupScheduleDialog
        open={openScheduleDialog}
        onSubmit={handleScheduleSubmit}
        isPending={isPending}
        onOpenChange={(value) => {
          setOpenScheduleDialog(value);
          if (!value) {
            setPendingGroupData(null);
            setPendingUserIds([]);
          }
        }}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent isClosable={isMainTab} className={'sm:h-[85dvh] sm:min-h-96 sm:max-w-108'} onCloseAutoFocus={handleClose} onEscapeKeyDown={handleClose}>
          <Form {...form}>
            <DialogBody>
              <LoadingOverlay isLoading={isPending}>
                <LoadingOverlaySpinner />
                <LoadingOverlayLabel>Trwa tworzenie grupy...</LoadingOverlayLabel>
              </LoadingOverlay>
              <GroupDialogHeader
                isMainTab={isMainTab}
                title={'Utwórz Grupę'}
                description={'Wprowadź nazwę grupy i dodaj do niej członków.'}
                searchUserValue={searchUserValue}
                onSearchValueChange={setSearchUserValue}
                onBackToMain={() => setTabs('main')}
              />
              <Tabs value={tabs} onValueChange={onTabsValueChange} className={'flex grow flex-col gap-3 overflow-auto p-4'}>
                <TabsContent value={'main'}>
                  <GroupUpsertDetailsForm
                    formId="create-group-form"
                    control={form.control}
                    nameField={'name'}
                    descField={'desc'}
                    slotsField={'slots'}
                    onSubmit={form.handleSubmit((data) =>
                      onSubmit(
                        data,
                        selectedUsers.map((u) => u.id)
                      )
                    )}
                  />
                  <SelectedUsersSummary selectedUsers={selectedUsers} slotsValue={slotsValue} onRemoveUser={onRemoveUser} onOpenSelector={() => onTabsValueChange('selectedList')} />
                </TabsContent>
                <TabsContent value={'selectedList'}>
                  <UsersSelectList
                    selectedUsers={selectedUsers}
                    onSelectionChange={setSelectedUsersFromIds}
                    users={users}
                    usersErrorMessage={usersErrorMessage}
                    usersIsEmpty={usersIsEmpty}
                    usersNoResults={usersNoResults}
                    getUsersInfinite={getUsersInfinite}
                  />
                </TabsContent>
              </Tabs>
            </DialogBody>
            {isMainTab ? (
              <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
                <Button onClick={handleClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
                  Anuluj
                </Button>
                <ButtonGroup className={'w-full md:w-fit'}>
                  <Button disabled={isPending} type={'button'} onClick={() => handleSaveAction('save')} variant={'primary'} className={'grow md:grow-0'}>
                    <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                      <CheckCircle2Icon />
                      Zapisz
                    </LoadingSwap>
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button disabled={isPending} type={'button'} variant={'primary'} className={'px-2.5'} aria-label={'Pokaż więcej akcji zapisu'}>
                        <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-full max-w-40">
                      <DropdownMenuLabel>Sposób zapisu</DropdownMenuLabel>
                      <DropdownMenuGroup>
                        <DropdownMenuItem>
                          <div className={'flex flex-col'}>
                            <p>Utwórz grupę teraz</p>
                            <p className={'text-text-tertiary text-xs'}>Zapisze grupę od razu, bez harmonogramu.</p>
                          </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSaveAction('saveAndOpenSchedule')}>
                          <div className={'flex flex-col'}>
                            <p>Przejdź do harmonogramu</p>
                            <p className={'text-text-tertiary text-xs'}>Najpierw ustawisz harmonogram, potem utworzysz grupę.</p>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ButtonGroup>
              </DialogFooter>
            ) : (
              <DialogFooter className={'border-border-primary dark:bg-card bg-accent items-center justify-between border-t'}>
                <SlotsCounter studentsCount={countStudents(selectedUsers)} slotsValue={slotsValue} />
                <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'} className={'ml-atuo'}>
                  Zapisz
                </Button>
              </DialogFooter>
            )}
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
